import { trackProductEvent } from "../lib/product-analytics";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApplyToJobInput,
  CatalogApplyInput,
  CatalogConfirmApplyInput,
  CatalogTrackRedirectInput,
  EmployerApplicantMessageInput,
  EmployerApplicationStatusUpdateInput,
  UpdateAutoApplyRuleInput,
} from "@tabbio/types";
import { QUERY_KEYS } from "../lib/query-keys";
import {
  cancelCatalogFeedQueries,
  clearHiddenCatalogJobFeedSnapshot,
  clearHiddenCatalogJobFeedSnapshots,
  invalidateCatalogJobPeripheralState,
  removeCatalogJobFromFeedCaches,
  removeCatalogJobsFromFeedCaches,
  restoreHiddenCatalogJobFeedCaches,
  restoreHiddenCatalogJobsFeedCaches,
  restoreQuerySnapshots,
  updateCatalogJobUserStateCaches,
} from "../lib/catalog-job-cache";
import {
  applyToJob,
  confirmCatalogApply,
  dismissAutoApplyActivity,
  fetchAutoApplyActivity,
  fetchAutoApplyOverview,
  fetchCatalogApplyHistory,
  fetchCatalogApplyPreparation,
  fetchCatalogDiscoveredJobs,
  fetchCatalogExploredJobs,
  refreshCatalogJobs,
  fetchCatalogJob,
  fetchCatalogSavedJobs,
  hideCatalogJob,
  saveCatalogJob,
  runAutoApply,
  submitCatalogApply,
  trackCatalogRedirect,
  unhideCatalogJob,
  unsaveCatalogJob,
  updateAutoApplyRule,
  fetchEmployerApplicantDetail,
  fetchEmployerApplicants,
  fetchDiscoveredJobs,
  fetchJob,
  fetchJobApplications,
  sendEmployerApplicantMessage,
  updateEmployerApplicantStatus,
  type DiscoverJobsParams,
  type CatalogDiscoverJobsParams,
  type CatalogExploreJobsParams,
  type FetchEmployerApplicantsParams,
  type FetchApplicationsParams,
} from "../lib/jobs-api";

function serializeDiscoverParams(params: DiscoverJobsParams = {}) {
  return JSON.stringify({
    query: params.query ?? "",
    locations: params.locations ?? [],
    workModes: params.workModes ?? [],
    employmentTypes: params.employmentTypes ?? [],
    datePosted: params.datePosted ?? null,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
}

function serializeEmployerApplicantParams(
  params: FetchEmployerApplicantsParams = {},
) {
  return JSON.stringify({
    query: params.query ?? "",
    statuses: params.statuses ?? [],
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
  });
}

function serializeCatalogDiscoverParams(
  params: CatalogDiscoverJobsParams = {},
) {
  return JSON.stringify({
    query: params.query ?? "",
    locations: params.locations ?? [],
    cities: params.cities ?? [],
    workModes: params.workModes ?? [],
    employmentTypes: params.employmentTypes ?? [],
    sources: params.sources ?? [],
    applyModes: params.applyModes ?? [],
    datePosted: params.datePosted ?? null,
    cvId: params.cvId ?? null,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
}

function serializeCatalogExploreParams(params: CatalogExploreJobsParams = {}) {
  return JSON.stringify({
    query: params.query ?? "",
    locations: params.locations ?? [],
    cities: params.cities ?? [],
    workModes: params.workModes ?? [],
    employmentTypes: params.employmentTypes ?? [],
    sources: params.sources ?? [],
    applyModes: params.applyModes ?? [],
    datePosted: params.datePosted ?? null,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
    includeLowConfidence: params.includeLowConfidence ?? true,
    surfaceEligibleOnly: params.surfaceEligibleOnly ?? false,
  });
}

function getNextCatalogPageParam(lastPage: {
  page: number;
  limit: number;
  total: number;
}) {
  return lastPage.page * lastPage.limit < lastPage.total
    ? lastPage.page + 1
    : undefined;
}

export function useDiscoveredJobs(params: DiscoverJobsParams, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_DISCOVERY(serializeDiscoverParams(params)),
    queryFn: () => fetchDiscoveredJobs(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useCatalogDiscoveredJobs(
  params: CatalogDiscoverJobsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY(
      serializeCatalogDiscoverParams(params),
    ),
    queryFn: () => fetchCatalogDiscoveredJobs(params),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useInfiniteCatalogDiscoveredJobs(
  params: CatalogDiscoverJobsParams,
  enabled = true,
) {
  const initialPage = params.page ?? 1;
  const keyParams = { ...params, page: initialPage };

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY(
      `infinite:${serializeCatalogDiscoverParams(keyParams)}`,
    ),
    queryFn: ({ pageParam }) =>
      fetchCatalogDiscoveredJobs({
        ...params,
        page: pageParam,
      }),
    initialPageParam: initialPage,
    getNextPageParam: getNextCatalogPageParam,
    enabled,
    staleTime: 10 * 60 * 1000,
    // Keep the previous results visible (dimmed) during a filter/search refetch
    // instead of blanking to a skeleton. `isPlaceholderData` flags the stale window.
    placeholderData: keepPreviousData,
  });
}

export function useCatalogExploredJobs(
  params: CatalogExploreJobsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_EXPLORE(
      serializeCatalogExploreParams(params),
    ),
    queryFn: () => fetchCatalogExploredJobs(params),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInfiniteCatalogExploredJobs(
  params: CatalogExploreJobsParams,
  enabled = true,
) {
  const initialPage = params.page ?? 1;
  const keyParams = { ...params, page: initialPage };

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_EXPLORE(
      `infinite:${serializeCatalogExploreParams(keyParams)}`,
    ),
    queryFn: ({ pageParam }) =>
      fetchCatalogExploredJobs({
        ...params,
        page: pageParam,
      }),
    initialPageParam: initialPage,
    getNextPageParam: getNextCatalogPageParam,
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useJobDetail(jobId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_DETAIL(jobId),
    queryFn: () => fetchJob(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 30_000,
  });
}

export function useCatalogJobDetail(jobId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(jobId),
    queryFn: () => fetchCatalogJob(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 30_000,
  });
}

export function useJobApplications(
  params: FetchApplicationsParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_APPLICATIONS(params.jobId, params.limit),
    queryFn: () => fetchJobApplications(params),
    enabled,
    staleTime: 15_000,
  });
}

export function useCatalogApplyHistory(limit = 100, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_HISTORY(limit),
    queryFn: () => fetchCatalogApplyHistory(limit),
    enabled,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useCatalogSavedJobs(limit = 100, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_SAVED(limit),
    queryFn: () => fetchCatalogSavedJobs(limit),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

function invalidateCatalogJobState(
  queryClient: ReturnType<typeof useQueryClient>,
  jobId?: string | null,
) {
  queryClient.invalidateQueries({ queryKey: ["job-catalog"] });
  queryClient.invalidateQueries({ queryKey: ["job-catalog", "saved"] });
  queryClient.invalidateQueries({ queryKey: ["job-catalog", "history"] });
  if (jobId) {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(jobId),
    });
  }
}

/**
 * Fetches fresh postings for the user's target roles when the feed has nothing
 * to show. Both feeds are invalidated on success rather than patched: the
 * server re-ranks everything it just ingested, so refetching is the only way to
 * see where the new postings actually land.
 */
export function useRefreshCatalogJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => refreshCatalogJobs(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY().slice(0, 2),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.JOB_CATALOG_EXPLORE().slice(0, 2),
        }),
      ]);
    },
  });
}

export function useSaveCatalogJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => saveCatalogJob(jobId),
    onSuccess: (state, jobId) => {
      // Funnel F6's middle step; only a saved job that succeeded counts.
      trackProductEvent('job_saved', { source: 'catalog' });
      updateCatalogJobUserStateCaches(queryClient, jobId, state);
      invalidateCatalogJobState(queryClient, jobId);
    },
  });
}

export function useUnsaveCatalogJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => unsaveCatalogJob(jobId),
    onSuccess: (state, jobId) => {
      trackProductEvent('job_unsaved', { source: 'catalog' });
      updateCatalogJobUserStateCaches(queryClient, jobId, state);
      invalidateCatalogJobState(queryClient, jobId);
    },
  });
}

export function useHideCatalogJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => hideCatalogJob(jobId),
    onMutate: async (jobId) => {
      const feedSnapshots = removeCatalogJobFromFeedCaches(queryClient, jobId);
      void cancelCatalogFeedQueries(queryClient);
      return { feedSnapshots };
    },
    onError: (_error, jobId, context) => {
      restoreQuerySnapshots(queryClient, context?.feedSnapshots);
      clearHiddenCatalogJobFeedSnapshot(queryClient, jobId);
    },
    onSuccess: (state, jobId) => {
      updateCatalogJobUserStateCaches(queryClient, jobId, state);
      invalidateCatalogJobPeripheralState(queryClient, jobId);
    },
  });
}

export function useHideCatalogJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobIds: string[]) => {
      const uniqueJobIds = [...new Set(jobIds)];
      return Promise.all(
        uniqueJobIds.map(async (jobId) => ({
          jobId,
          state: await hideCatalogJob(jobId),
        })),
      );
    },
    onMutate: async (jobIds) => {
      const uniqueJobIds = [...new Set(jobIds)];
      const feedSnapshots = removeCatalogJobsFromFeedCaches(
        queryClient,
        uniqueJobIds,
      );
      void cancelCatalogFeedQueries(queryClient);
      return { feedSnapshots, jobIds: uniqueJobIds };
    },
    onError: (_error, _jobIds, context) => {
      restoreQuerySnapshots(queryClient, context?.feedSnapshots);
      clearHiddenCatalogJobFeedSnapshots(queryClient, context?.jobIds ?? []);
    },
    onSuccess: (results) => {
      for (const result of results) {
        updateCatalogJobUserStateCaches(
          queryClient,
          result.jobId,
          result.state,
        );
        invalidateCatalogJobPeripheralState(queryClient, result.jobId);
      }
    },
  });
}

export function useUnhideCatalogJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => unhideCatalogJob(jobId),
    onMutate: async (jobId) => {
      await cancelCatalogFeedQueries(queryClient);
      const feedSnapshots = restoreHiddenCatalogJobFeedCaches(
        queryClient,
        jobId,
      );
      return { feedSnapshots };
    },
    onError: (_error, _jobId, context) => {
      restoreQuerySnapshots(queryClient, context?.feedSnapshots);
    },
    onSuccess: (state, jobId) => {
      updateCatalogJobUserStateCaches(queryClient, jobId, state);
      clearHiddenCatalogJobFeedSnapshot(queryClient, jobId);
      invalidateCatalogJobPeripheralState(queryClient, jobId);
    },
  });
}

export function useUnhideCatalogJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobIds: string[]) => {
      const uniqueJobIds = [...new Set(jobIds)];
      return Promise.all(
        uniqueJobIds.map(async (jobId) => ({
          jobId,
          state: await unhideCatalogJob(jobId),
        })),
      );
    },
    onMutate: async (jobIds) => {
      const uniqueJobIds = [...new Set(jobIds)];
      await cancelCatalogFeedQueries(queryClient);
      const feedSnapshots = restoreHiddenCatalogJobsFeedCaches(
        queryClient,
        uniqueJobIds,
      );
      return { feedSnapshots, jobIds: uniqueJobIds };
    },
    onError: (_error, _jobIds, context) => {
      restoreQuerySnapshots(queryClient, context?.feedSnapshots);
    },
    onSuccess: (results, _jobIds, context) => {
      for (const result of results) {
        updateCatalogJobUserStateCaches(
          queryClient,
          result.jobId,
          result.state,
        );
        invalidateCatalogJobPeripheralState(queryClient, result.jobId);
      }
      clearHiddenCatalogJobFeedSnapshots(
        queryClient,
        context?.jobIds ?? results.map((result) => result.jobId),
      );
    },
  });
}

export function useCatalogApplyPreparation(jobId: string, enabled = true) {
  return useQuery({
    queryKey: [...QUERY_KEYS.JOB_CATALOG_DETAIL(jobId), "prepare"] as const,
    queryFn: () => fetchCatalogApplyPreparation(jobId, {}),
    enabled: enabled && Boolean(jobId),
    staleTime: 15_000,
  });
}

export function useCatalogAutoApplyOverview(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
    queryFn: () => fetchAutoApplyOverview(),
    enabled,
    staleTime: 20_000,
  });
}

export function useCatalogAutoApplyActivity(
  limit = 10,
  enabled = true,
  cursor?: string | null,
) {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY_ACTIVITY(limit, cursor),
    queryFn: () => fetchAutoApplyActivity(limit, cursor ?? undefined),
    enabled,
    staleTime: 20_000,
  });
}

export function useUpdateCatalogAutoApplyRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ruleKey,
      input,
    }: {
      ruleKey: string;
      input: UpdateAutoApplyRuleInput;
    }) => updateAutoApplyRule(ruleKey, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY_ACTIVITY_ROOT,
      });
      queryClient.invalidateQueries({ queryKey: ["cvs"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useRunCatalogAutoApply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ruleKey: string) => runAutoApply(ruleKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY_ACTIVITY_ROOT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_HISTORY(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY(),
      });
    },
  });
}

export function useDismissCatalogAutoApplyActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: string) => dismissAutoApplyActivity(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY_ACTIVITY_ROOT,
      });
    },
  });
}

export function useCatalogApplyJob(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CatalogApplyInput) => submitCatalogApply(jobId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(jobId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_HISTORY(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY_ACTIVITY_ROOT,
      });
    },
  });
}

export function useCatalogTrackRedirect(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CatalogTrackRedirectInput) =>
      trackCatalogRedirect(jobId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(jobId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_HISTORY(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY(),
      });
    },
  });
}

export function useConfirmCatalogApply(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CatalogConfirmApplyInput) =>
      confirmCatalogApply(jobId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(jobId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_HISTORY(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_DISCOVERY(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_EXPLORE(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY_ACTIVITY_ROOT,
      });
    },
  });
}

export function useApplyToJob(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ApplyToJobInput) => applyToJob(jobId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs", "discover"] });
      queryClient.invalidateQueries({ queryKey: ["jobs", "applications"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOB_DETAIL(jobId) });
    },
  });
}

export function useEmployerApplicants(
  jobId: string,
  params: FetchEmployerApplicantsParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.EMPLOYER_APPLICANTS(
      jobId,
      serializeEmployerApplicantParams(params),
    ),
    queryFn: () => fetchEmployerApplicants(jobId, params),
    enabled: enabled && Boolean(jobId),
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}

export function useEmployerApplicantDetail(
  jobId: string,
  applicationId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.EMPLOYER_APPLICANT_DETAIL(jobId, applicationId),
    queryFn: () => fetchEmployerApplicantDetail(jobId, applicationId),
    enabled: enabled && Boolean(jobId) && Boolean(applicationId),
    staleTime: 15_000,
  });
}

export function useUpdateEmployerApplicantStatus(
  jobId: string,
  applicationId: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: EmployerApplicationStatusUpdateInput) =>
      updateEmployerApplicantStatus(applicationId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs", "company"] });
      // Company-wide Applications inbox lists the same applications.
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMPANY_APPLICATIONS_ROOT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMPLOYER_APPLICANTS(jobId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMPLOYER_APPLICANT_DETAIL(jobId, applicationId),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
      });
    },
  });
}

export function useSendEmployerApplicantMessage(
  jobId: string,
  applicationId: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: EmployerApplicantMessageInput) =>
      sendEmployerApplicantMessage(applicationId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs", "company"] });
      // Messaging counts as activity: the inbox sorts by latest activity.
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMPANY_APPLICATIONS_ROOT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMPLOYER_APPLICANTS(jobId),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNREAD_COUNT });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMPLOYER_APPLICANT_DETAIL(jobId, applicationId),
      });
    },
  });
}
